/**
 * CompanyLocationFieldset - Shared fieldset molecule for selecting a company
 * and a location filtered by that company.
 *
 * Used by the JobForm organism for both work order and inspection creation.
 * Handles:
 *	 - Company SearchableSelect (deduped, sorted alphabetically)
 *	 - Location SearchableSelect (filtered by selected company, deduped, sorted)
 *	 - Auto-fill of project address from the selected location's stored address
 *
 * All dropdown data is fetched from the API; this component manages its own
 * loading states for companies and locations.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../../services/api';
import { SearchableSelect } from '../SearchableSelect';
import { LinkButton } from '../../atoms/LinkButton';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Minimal company record for the dropdown */
interface DropdownCompany {
	/** Normalized company ID (may come as company_id from API) */
	id: string | number;
	/** Company display name */
	name: string;
}

/** Location record for the dropdown, includes address fields for auto-fill */
interface DropdownLocation {
	/** Location primary key */
	id: string | number;
	/** Location display name */
	name: string;
	/** Street address for project address auto-fill */
	address?: string;
	/** City for project address auto-fill */
	city?: string;
	/** State abbreviation for project address auto-fill */
	state?: string;
	/** ZIP code for project address auto-fill */
	zip?: string;
}

/** Props for the CompanyLocationFieldset molecule */
export interface CompanyLocationFieldsetProps {
	/** Currently selected company ID */
	companyId: string;
	/** Callback when company selection changes */
	onCompanyChange: (companyId: string) => void;
	/** Currently selected location ID */
	locationId: string;
	/** Callback when location selection changes */
	onLocationChange: (locationId: string) => void;
	/** Called with the auto-filled address string when a location is selected */
	onAddressAutoFill?: (address: string) => void;
	/** Inline validation errors keyed by field name */
	errors?: Record<string, string>;
	/** Standard label CSS class for consistent styling */
	labelClass: string;
	/** Standard input CSS class for consistent sizing */
	inputClass: string;
	/** Normal border class (non-error state) */
	normalBorder: string;
	/** Error border class (validation error state) */
	errorBorder: string;
	/** When true, disable the Company dropdown — used by the Follow-Up create
	 *  flow so the user can't change the customer away from the parent's. */
	lockCompany?: boolean;
	/** When true, disable the Building dropdown — used by the
	 *  child-WO-under-a-Project create flow so the WO inherits the
	 *  project's building and can't drift to a different site. */
	lockLocation?: boolean;
	/**
	 * When provided, an "Add a New Building" link renders under the Building
	 * dropdown once a company is picked. The parent owns the modal (creating
	 * a building is an organism-level concern — see AddBuildingModal), so
	 * this fieldset only surfaces the affordance and reports the click.
	 * Leave it unset to hide the link entirely, e.g. for roles that can't
	 * create locations.
	 */
	onAddBuilding?: () => void;
	/**
	 * Bump this to force a re-fetch of the building list. The parent bumps it
	 * after creating a building so the new one appears in the dropdown without
	 * the user having to reselect the company.
	 */
	refreshToken?: number;
	/** Optional extra classes appended to the root element. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const CompanyLocationFieldset: React.FC<CompanyLocationFieldsetProps> = ({
	companyId,
	onCompanyChange,
	locationId,
	onLocationChange,
	onAddressAutoFill,
	errors = {},
	labelClass,
	inputClass,
	normalBorder,
	errorBorder,
	lockCompany = false,
	lockLocation = false,
	onAddBuilding,
	refreshToken = 0,
	className = '',
}) => {
	/* --- Dropdown data state --- */
	/** Full list of companies fetched from the API */
	const [companies, setCompanies] = useState<DropdownCompany[]>([]);
	/** Locations filtered by the selected company */
	const [locations, setLocations] = useState<DropdownLocation[]>([]);

	/* --- Loading indicators for each dropdown data source --- */
	const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
	const [isLoadingLocations, setIsLoadingLocations] = useState(false);

	/* ============================================
	 * DATA FETCHING -- Companies (on mount)
	 * ============================================ */

	/** Fetches the full list of companies for the company dropdown */
	useEffect(() => {
		const loadCompanies = async () => {
			setIsLoadingCompanies(true);
			try {
				/* per_page=999 avoids pagination truncating the dropdown */
				const res = await api.get<any>('/companies', { per_page: '999' });
				const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
				/* Normalize company_id (Laravel PK) to id for the dropdown */
				setCompanies(data.map((c: any) => ({ id: c.company_id || c.id, name: c.name })));
			} catch (err) {
				console.error('[CompanyLocationFieldset] Failed to load companies:', err);
			} finally {
				setIsLoadingCompanies(false);
			}
		};
		loadCompanies();
	}, []);

	/* ============================================
	 * DATA FETCHING -- Locations (filtered by company)
	 * ============================================ */

	/** Re-fetches locations whenever the selected company changes -- or when
	 *  refreshToken is bumped after a building was created from this form. */
	useEffect(() => {
		if (!companyId) {
			/* No company selected -- clear locations */
			setLocations([]);
			return;
		}

		const loadLocations = async () => {
			setIsLoadingLocations(true);
			try {
				/* per_page=999 avoids pagination truncating the dropdown */
				const res = await api.get<any>('/locations', { company_id: companyId, per_page: '999' });
				const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
				setLocations(data);
			} catch (err) {
				console.error('[CompanyLocationFieldset] Failed to load locations:', err);
			} finally {
				setIsLoadingLocations(false);
			}
		};

		loadLocations();
	}, [companyId, refreshToken]);

	/* ============================================
	 * LOCATION CHANGE HANDLER -- auto-fills address
	 * ============================================ */

	/** When a location is selected, build a full address string and notify parent */
	const handleLocationChange = useCallback((newLocationId: string) => {
		onLocationChange(newLocationId);
		if (newLocationId && onAddressAutoFill) {
			const selectedLoc = locations.find((loc) => String(loc.id) === newLocationId);
			if (selectedLoc) {
				/* Build a comma-separated address from available fields */
				const parts = [selectedLoc.address, selectedLoc.city, selectedLoc.state, selectedLoc.zip].filter(Boolean);
				onAddressAutoFill(parts.join(', '));
			}
		}
	}, [locations, onLocationChange, onAddressAutoFill]);

	/* ============================================
	 * RENDER
	 * ============================================ */

	return (
		<div className={`company-location-fieldset space-y-16px ${className}`} data-ref="molecule-company-location-fieldset">
			{/* Company SearchableSelect (required, deduped + sorted A-Z) */}
			<div className="company-location-fieldset__field">
				<label htmlFor="companyId" className={labelClass}>Company <span className="text-red-500">*</span></label>
				<SearchableSelect id="companyId" options={dedupeAndSort(companies.map((c) => ({ value: String(c.id), label: c.name })))} value={companyId} onChange={onCompanyChange} placeholder={isLoadingCompanies ? 'Loading companies...' : 'Select company'} hasError={!!errors.companyId} disabled={isLoadingCompanies || lockCompany} />
				{errors.companyId && <p className="mt-4px text-14px text-red-600">{errors.companyId}</p>}
			</div>

			{/* Location dropdown (required, filtered by company, deduped + sorted A-Z) */}
			<div className="company-location-fieldset__field">
				<label htmlFor="locationId" className={labelClass}>Building <span className="text-red-500">*</span></label>
				<select id="locationId" value={locationId} onChange={(e) => handleLocationChange(e.target.value)} disabled={!companyId || isLoadingLocations || lockLocation} className={`${inputClass} bg-white ${errors.locationId ? errorBorder : normalBorder} disabled:opacity-50 disabled:cursor-not-allowed`}>
					<option value="">{!companyId ? 'Select a company first' : isLoadingLocations ? 'Loading buildings...' : 'Select building'}</option>
					{dedupeAndSort(locations.map((loc) => ({ value: String(loc.id), label: loc.name }))).map((opt) => (
						<option key={opt.value} value={opt.value}>{opt.label}</option>
					))}
				</select>
				{errors.locationId && <p className="mt-4px text-14px text-red-600">{errors.locationId}</p>}
				{companyId && !isLoadingLocations && locations.length === 0 && (
					<p className="mt-4px text-12px text-brand-text-muted">No buildings found for this company.</p>
				)}

				{/* "Add a New Building" escape hatch -- the customer's building often
					isn't in the system yet when the job is being written up, and
					bouncing to the Buildings page would lose the half-filled form.
					Hidden until a company is chosen (the new building has to be
					assigned to one) and while the Building field is locked. */}
				{onAddBuilding && companyId && !lockLocation && (
					<LinkButton onClick={onAddBuilding} className="company-location-fieldset__add-building mt-4px" dataRef="molecule-company-location-fieldset-add-building">
						+ Add a New Building
					</LinkButton>
				)}
			</div>
		</div>
	);
};

export default CompanyLocationFieldset;
